import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { smartFormApi } from '@/services';
import type { ApiResponse } from '@/types/api-types';
import type { paths } from '~/generated/smart-form/api-schema';

const KNOWN_EVENT_NAMES = [
  'admissionAndDischarge',
  'completeAdmission',
  'completeDischarge',
  'transferToICU',
  'writeOrders',
  'rescue',
  'surgery',
] as const;

const eventNameSchema = z
  .enum([...KNOWN_EVENT_NAMES, 'unknown'])
  .catch((ctx) => {
    console.warn('[event-list] unknown event name', {
      value: ctx.value,
      issues: ctx.issues,
    });

    return 'unknown';
  });

export type EventNameSchema = z.infer<typeof eventNameSchema>;

const categorySchema = z.object({
  id: z.string(),
  code: z.string(),
  displayName: z.string(),
});

const formSchema = z.object({
  id: z.string(),
  templateId: z.string(),
  name: z.string(),
  templateCode: z.string(),
  category: categorySchema,
  type: categorySchema,
  specialty: categorySchema,
  lockVersion: z.number(),
  disabled: z.boolean(),
});
type FormSchema = z.infer<typeof formSchema>;

export type Event = {
  id: string;
  name: EventNameSchema;
  parentId: string | null;
  children: Event[];
  forms: FormSchema[];
};

const apiResponseSchema: z.ZodType<Event[]> = z.array(
  z.object({
    id: z.string(),
    name: eventNameSchema,
    parentId: z.string().nullable(),
    children: z.lazy(() => apiResponseSchema).default([]),
    forms: z.array(formSchema),
  }),
);

const PATH = '/v1/event/queryEvent' as const;

export { PATH as QUERY_EVENT_KEY };

type QueryEvent = typeof PATH;
type APIResponse = ApiResponse<paths, QueryEvent>;

export const getEventList = async (signal?: AbortSignal) => {
  const { data: response } = await smartFormApi.get<APIResponse>(PATH, {
    signal,
  });

  const result = apiResponseSchema.safeParse(response.data);

  if (!result.success) {
    throw new Error(result.error.message);
  }

  return result.data;
};

export const getEventListQueryOptions = () =>
  queryOptions({
    queryKey: [PATH],
    queryFn: ({ signal }) => getEventList(signal),
  });

export const useSuspenseQueryEventList = () => {
  const { data } = useSuspenseQuery(getEventListQueryOptions());
  return {
    data,
  };
};
